import { useEffect, useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm, useFormState } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useIntake } from '../useIntake';
import { useStore } from '../../store';
import {
  createEpisodeStepFormSchema,
  type CreateEpisodeStepFormData,
} from '../../schemas/createEpisodeStepFormSchema';
import { getReferralDocs, client } from '../../services/api';

import { useFormAutoSave } from './useFormAutoSave';
import { usePatientMatching } from './usePatientMatching';
import { useAddressValidation } from './useAddressValidation';
import { useClinicData } from './useClinicData';
import { useKinnserCreation } from './useKinnserCreation';
import { useKinnserOnboarding, type S3File } from './useKinnserOnboarding';
import { useRogerCreation } from './useRogerCreation';
import { useFormInitialization } from './useFormInitialization';
import { useMrnValidation } from './useMrnValidation';
import { CREATE_EPISODE_STEP_DEFAULT_VALUES } from './constants';
import type { DisplayValues } from './types';
import { FeatureFlag } from '../../constants/featureFlags';
import { formatDateToISO } from '../../utils/dateUtils';

export function useCreateEpisodeStepForm() {
  const { intakeId } = useParams<{ intakeId: string }>();
  const { selectedIntake, updateIntakeRecord } = useIntake();
  const user = useStore(state => state.user);
  const hasPermission = useStore(state => state.hasPermission);

  // React Hook Form setup
  const {
    control,
    register,
    reset,
    watch,
    setValue,
    trigger,
    clearErrors,
    getValues,
    handleSubmit,
    setError,
  } = useForm<CreateEpisodeStepFormData>({
    resolver: zodResolver(createEpisodeStepFormSchema),
    defaultValues: CREATE_EPISODE_STEP_DEFAULT_VALUES,
    mode: 'onChange',
  });

  // Subscribe to form state changes (errors, isValid) separately for proper reactivity
  const { errors, dirtyFields, isValid } = useFormState({ control });

  // Watch form values for cascading logic (dropdowns, etc.)
  const kinnserClinicId = watch('kinnser_clinic_id');
  const hhCahpsOptOut = watch('kinnser_hh_cahps_opt_out');
  const kinnserLanguageKey = watch('kinnser_language_key');
  const pushToRoger = watch('push_to_roger');
  const pushToKinnser = watch('push_to_kinnser');

  // Validate Other Language field when Language dropdown changes
  useEffect(() => {
    if (kinnserLanguageKey === '6') {
      // When "Other/Unknown" is selected, trigger validation on the other language field
      trigger('kinnser_other_language');
    } else {
      // When any other language is selected, clear any validation errors on the other language field
      clearErrors('kinnser_other_language');
    }
  }, [kinnserLanguageKey, trigger, clearErrors]);

  // Auto-save hook - saves on blur, then resets baseline
  const { saveStatus, saveToServer, handleFieldBlur } = useFormAutoSave({
    intakeId,
    selectedIntake,
    updateIntakeRecord,
    control,
    getValues,
    trigger,
    reset,
  });

  // Patient matching hook
  const patientMatching = usePatientMatching({
    kinnserClinicId,
  });

  // Address validation hook
  const addressValidation = useAddressValidation({
    setValue,
    getValues,
    saveToServer,
  });

  // Clinic data hook
  const { clinics, clinicData, loadingClinics, loadingClinicData } =
    useClinicData({
      agencySystemName: user?.agency?.system_name,
      kinnserClinicId,
      selectedIntake,
      setValue,
      trigger,
      intakeId,
      saveToServer,
    });

  // Watch MRN value for conditional validation
  const kinnserMrn = watch('kinnser_mrn');

  // Conditionally validate MRN based on auto_mrn setting
  useMrnValidation({
    clinicData,
    loadingClinicData,
    mrnValue: kinnserMrn,
    clearErrors,
    setError,
  });

  // State for S3 files of intake documents (needed for onboarding)
  const [s3Files, setS3Files] = useState<S3File[]>([]);
  const hasFeatureFlag = useStore(state => state.hasFeatureFlag);

  // Check if episode creation is enabled (from Scheduling step)
  const shouldCreateEpisode: boolean = !!(
    selectedIntake?.kinnser_create_episode ||
    selectedIntake?.roger_create_episode
  );
  // Check feature flag to determine which hook to use
  const hasIntakeDocUploadFlag = hasFeatureFlag(FeatureFlag.INTAKE_DOC_UPLOAD);

  // Kinnser onboarding hook (used when episode creation is enabled and feature flag is true)
  const onboardingHook = useKinnserOnboarding<CreateEpisodeStepFormData>({
    intakeId,
    trigger,
    getValues,
    tabName: user?.agency?.hnp_upload_tab ?? null,
    taskName: user?.agency?.hnp_upload_task ?? null,
    s3Files: s3Files,
  });

  // Kinnser creation hook (used when episode creation is disabled or feature flag is false)
  const creationHook = useKinnserCreation({
    intakeId,
    trigger,
  });

  // Roger creation hook
  const rogerCreationHook = useRogerCreation({
    intakeId,
    trigger,
  });

  // Normalize return values based on which hook should be active for kinnser
  const shouldUseOnboarding = shouldCreateEpisode && hasIntakeDocUploadFlag;
  const handleCreateInKinnser = shouldUseOnboarding
    ? onboardingHook.handleOnboardInKinnser
    : creationHook.handleCreateInKinnser;

  // Combine creating in EMR states for kinnser and roger
  const creatingInKinnser = shouldUseOnboarding
    ? onboardingHook.onboardingInKinnser
    : creationHook.creatingInKinnser;
  const creatingInRoger = rogerCreationHook.creatingInRoger;
  const creatingInEMR: boolean = !!(
    (pushToRoger && creatingInRoger) ||
    (pushToKinnser && creatingInKinnser)
  );

  // Combine create results for kinnser and roger
  const kinnserCreateResult = shouldUseOnboarding
    ? onboardingHook.onboardResult
    : creationHook.createResult;
  const rogerCreateResult = rogerCreationHook.rogerCreateResult;
  const createResult = pushToRoger
    ? rogerCreateResult
    : pushToKinnser
      ? kinnserCreateResult
      : null;

  // Form initialization hook
  useFormInitialization({
    selectedIntake,
    reset,
    setValue,
    trigger,
    saveToServer,
  });

  // Display values derived from watched form values
  const watchedDob = watch('patient_dob');
  const intakeDob = selectedIntake?.patient_dob;
  const dobValue = watchedDob || intakeDob || '';

  const displayValues: DisplayValues = {
    firstName:
      watch('patient_first_name') || selectedIntake?.patient_first_name || '',
    lastName:
      watch('patient_last_name') || selectedIntake?.patient_last_name || '',
    dob: typeof dobValue === 'string' ? dobValue : '',
    phone: watch('patient_phone') || selectedIntake?.patient_phone || '',
    notes: watch('patient_notes') || selectedIntake?.patient_notes || '',
  };

  // Specialized blur handler for name fields - triggers save and patient matching
  const handleNameFieldBlur = useCallback(
    (fieldEvent: React.FocusEvent<HTMLElement>) => {
      const target = fieldEvent.target as HTMLInputElement;
      // Try name attribute first, fall back to data-field-name attribute
      const fieldName = (target.name ||
        target.dataset.fieldName) as keyof CreateEpisodeStepFormData;

      if (!dirtyFields.patient_first_name && !dirtyFields.patient_last_name)
        return;

      setValue(fieldName, target.value);
      handleFieldBlur(fieldEvent);

      // Defer the match check to allow the blur to complete
      setTimeout(() => {
        const values = getValues();
        if (values.patient_first_name && values.patient_last_name) {
          patientMatching.checkPatientMatch({
            firstName: values.patient_first_name,
            middleName: values.patient_middle_name,
            lastName: values.patient_last_name,
            mrn: values.kinnser_mrn,
            dob: values.patient_dob,
            ssn: values.patient_ssn,
            address: values.patient_street_address,
            zipCode: values.patient_zip_code,
            gender: values.patient_gender,
            email: values.patient_email,
            city: values.patient_city,
            state: values.patient_state,
          });
        }
      }, 0);
    },
    [
      handleFieldBlur,
      getValues,
      patientMatching,
      dirtyFields.patient_first_name,
      dirtyFields.patient_last_name,
    ]
  );

  // Specialized blur handler for address fields - triggers save and address validation
  const handleAddressFieldBlur = useCallback(
    (fieldNameOrEvent: React.FocusEvent<HTMLElement>) => {
      if (!dirtyFields.patient_street_address && !dirtyFields.patient_zip_code)
        return;

      handleFieldBlur(fieldNameOrEvent);

      // Defer the validation check to allow the blur to complete
      setTimeout(() => {
        const values = getValues();
        if (
          values.patient_street_address &&
          values.patient_zip_code &&
          !values.push_to_roger
        ) {
          addressValidation.checkAddressValidation(
            values.patient_street_address,
            values.patient_zip_code
          );
        }
      }, 0);
    },
    [
      handleFieldBlur,
      getValues,
      addressValidation,
      dirtyFields.patient_street_address,
      dirtyFields.patient_zip_code,
    ]
  );

  // Specialized blur handler for clinic field - resets dependent fields
  const handleClinicFieldBlur = useCallback(() => {
    if (!dirtyFields.kinnser_clinic_id) return;

    // Reset dependent fields in form
    setValue('kinnser_physician_id', null);
    setValue('kinnser_branch_id', null);
    setValue('kinnser_insurance_provider_id', null);

    // Save clinic change along with reset dependent fields in one API call
    saveToServer({
      kinnser_clinic_id: getValues('kinnser_clinic_id'),
      kinnser_physician_id: '',
      kinnser_branch_id: '',
      kinnser_insurance_provider_id: '',
    });
  }, [getValues, setValue, saveToServer, dirtyFields.kinnser_clinic_id]);

  // Track if initial patient matching has been done
  const initialMatchDoneRef = useRef(false);

  // Perform patient matching only once on initial load
  useEffect(() => {
    // Skip if already done initial matching
    if (initialMatchDoneRef.current) return;

    if (selectedIntake && kinnserClinicId && !pushToRoger) {
      const firstName = selectedIntake.patient_first_name;
      const lastName = selectedIntake.patient_last_name;
      if (firstName && lastName) {
        initialMatchDoneRef.current = true;
        patientMatching.checkPatientMatch({
          firstName: selectedIntake.patient_first_name,
          middleName: selectedIntake.patient_middle_name,
          lastName: selectedIntake.patient_last_name,
          mrn: selectedIntake.kinnser_mrn,
          dob: selectedIntake.patient_dob
            ? typeof selectedIntake.patient_dob === 'string'
              ? selectedIntake.patient_dob
              : formatDateToISO(selectedIntake.patient_dob)
            : null,
          ssn: selectedIntake.patient_ssn,
          address: selectedIntake.patient_street_address,
          zipCode: selectedIntake.patient_zip_code,
          gender: selectedIntake.patient_gender,
          email: selectedIntake.patient_email,
          city: selectedIntake.patient_city,
          state: selectedIntake.patient_state,
        });
      }
    }
  }, [selectedIntake, kinnserClinicId, patientMatching, pushToRoger]);

  const { resetMatches } = patientMatching;
  const { resetValidation } = addressValidation;

  // Reset patient matching and address validation when pushing to Roger
  useEffect(() => {
    if (pushToRoger) {
      resetMatches();
      resetValidation();
    }
  }, [pushToRoger, resetMatches, resetValidation]);

  // Fetch S3 URLs for all intake documents (only needed when onboarding is enabled)
  useEffect(() => {
    if (!shouldUseOnboarding) return;

    const fetchS3Files = async () => {
      if (!intakeId) return;

      try {
        const response = await getReferralDocs({
          path: { intake_id: intakeId },
          client,
        });

        const documents = response.data?.documents;
        if (documents && Array.isArray(documents) && documents.length > 0) {
          const files: S3File[] = (
            documents as unknown as Array<{
              url: string;
              file_name: string;
            }>
          ).map(doc => ({
            url: doc.url,
            filename: doc.file_name,
          }));
          setS3Files(files);
        }
      } catch (error) {
        console.error('Error fetching S3 URLs:', error);
        // Don't set error state - just log it, s3Files will remain empty
        // and useKinnserOnboarding will handle the validation
      }
    };

    fetchS3Files();
  }, [intakeId, shouldUseOnboarding]);

  return {
    // Route params
    intakeId,

    // Intake data
    selectedIntake,

    // Permissions
    hasPermission,

    // Form
    control,
    register,
    errors,
    setValue,
    getValues,
    handleSubmit,
    handleFieldBlur,

    // Watched values
    hhCahpsOptOut,
    kinnserLanguageKey,
    pushToRoger,
    pushToKinnser,

    // Display values
    displayValues,

    // Save status
    saveStatus,
    saveToServer,

    // Clinics
    clinics,
    clinicData,
    loadingClinics,
    loadingClinicData,

    // Form validation
    isValid,

    // Create Episode Generic fields
    creatingInEMR,
    createResult,

    // Kinnser creation/onboarding
    handleCreateInKinnser,
    isUsingOnboarding: shouldUseOnboarding,

    // Roger creation
    handleCreateInRoger: rogerCreationHook.handleCreateInRoger,

    // Patient matching
    patientMatches: patientMatching.patientMatches,
    patientMatchModalOpen: patientMatching.patientMatchModalOpen,
    setPatientMatchModalOpen: patientMatching.setPatientMatchModalOpen,
    isMatchingPatients: patientMatching.isMatchingPatients,
    handleNameFieldBlur,

    // Address validation
    addressSuggestion: addressValidation.addressSuggestion,
    addressModalOpen: addressValidation.addressModalOpen,
    setAddressModalOpen: addressValidation.setAddressModalOpen,
    isValidatingAddress: addressValidation.isValidatingAddress,
    handleAddressFieldBlur,
    handleAcceptAddressSuggestion:
      addressValidation.handleAcceptAddressSuggestion,

    // Clinic field handler
    handleClinicFieldBlur,
  };
}
